/**
 * Shared Feature Flag Hooks — Factory Module
 *
 * Contains all 27 composed feature flag hooks that are identical across
 * native and web platforms. Each platform file provides its own
 * `useFeatureFlag` primitive; this module consumes it via a factory.
 *
 * Architecture: ADR-001 — PostHog flags are kill switches / gradual rollout
 * tools. Tier enforcement is always Supabase-authoritative.
 */

export type SignupFlowVariant = 'control' | 'optimized';

/**
 * Primitive hook contract — provided by each platform file.
 *
 * Returns undefined while PostHog is loading, boolean/string once ready.
 */
export type UseFeatureFlagFn = (flagKey: string) => boolean | string | undefined;

/**
 * Factory that creates all 27 composed feature flag hooks from a
 * platform-specific `useFeatureFlag` primitive.
 */
export function createFeatureFlagHooks(useFlag: UseFeatureFlagFn) {
  // -- Phase 1.7 individual hooks ------------------------------------------

  /** Google Sign-in button enabled */
  function useGoogleSignInFlag(): boolean {
    return useFlag('google_signin_enabled') === true;
  }

  /** Early email capture prompt enabled */
  function useEmailCaptureFlag(): boolean {
    return useFlag('early_email_capture_enabled') === true;
  }

  /** Signup form variant for A/B testing */
  function useSignupVariantFlag(): SignupFlowVariant {
    const flag = useFlag('signup_flow_variant');
    return flag === 'optimized' ? 'optimized' : 'control';
  }

  /**
   * @deprecated 100% rollout complete — always returns true.
   */
  function useV8LetterThrowingFlag(): boolean {
    return true;
  }

  /** A/B Test: Collapsible accordion sections for DramaX tier */
  function useL3AccordionsFlag(): boolean {
    return useFlag('l3_accordions_enabled') === true;
  }

  // -- Kill switch flags (dd-feat-*) — default ON --------------------------

  /** Kill switch for PatternSpotlight (Drama+ feature). Default: enabled. */
  function usePatternSpotlightFlag(): boolean {
    return useFlag('dd-feat-pattern-spotlight') !== false;
  }

  /** Kill switch for TransformationInsight (DramaX feature). Default: enabled. */
  function useTransformationInsightFlag(): boolean {
    return useFlag('dd-feat-transformation-insight') !== false;
  }

  /** Kill switch for shared analysis links. Default: enabled. */
  function useSharedLinksFlag(): boolean {
    return useFlag('dd-feat-shared-links') !== false;
  }

  /** Kill switch for group analysis (DramaX feature). Default: enabled. */
  function useGroupAnalysisFlag(): boolean {
    return useFlag('dd-feat-group-analysis') !== false;
  }

  /** Kill switch for history pattern trends (Drama+ feature). Default: enabled. */
  function useHistoryPatternsFlag(): boolean {
    return useFlag('dd-feat-history-patterns') !== false;
  }

  /** Kill switch for multimodal input (DramaX feature). Default: enabled. */
  function useMultimodalFlag(): boolean {
    return useFlag('dd-feat-multimodal') !== false;
  }

  /** Kill switch for generative results (SDUI json-render pipeline). Default: enabled. */
  function useGenerativeResultsFlag(): boolean {
    return useFlag('dd-feat-generative-results') !== false;
  }

  /** Kill switch for onboarding flow (DD-159). Default: enabled. */
  function useOnboardingFlowFlag(): boolean {
    return useFlag('onboarding_flow_enabled') !== false;
  }

  /** Kill switch for re-engagement What's New modal (DD-160). Default: enabled. */
  function useReEngagementWhatsNewFlag(): boolean {
    return useFlag('re_engagement_whats_new_enabled') !== false;
  }

  /** A/B Test: Blur teaser conversion UX (DD-157). Default: disabled. */
  function useDramaPlusConversionUxFlag(): boolean {
    return useFlag('drama_plus_conversion_ux') === true;
  }

  /** Opt-in: streaming SDUI results (#1158.P0.4). Default: disabled. */
  function useStreamingResultsFlag(): boolean {
    return useFlag('dd-feat-streaming-results') === true;
  }

  // -- Kill switch flags (ff_*) — Wave 1: Revenue Protection ---------------

  /** Kill switch for all sharing functionality. Default: enabled. */
  function useSharingEnabledFlag(): boolean {
    return useFlag('ff_sharing_enabled') !== false;
  }

  /** Kill switch for OG image generation. Default: enabled. */
  function useOgImageGenerationFlag(): boolean {
    return useFlag('ff_og_image_generation') !== false;
  }

  /** Kill switch for self-healing validation pipeline. Default: enabled. */
  function useSelfHealingValidationFlag(): boolean {
    return useFlag('ff_self_healing_validation') !== false;
  }

  // -- Wave 2: Monetization Control ----------------------------------------

  /** Kill switch for SDUI rendering pipeline. Default: enabled. */
  function useSduiRenderingFlag(): boolean {
    return useFlag('ff_sdui_rendering') !== false;
  }

  /** Kill switch for message rewrites/transformations. Default: enabled. */
  function useRewritesEnabledFlag(): boolean {
    return useFlag('ff_rewrites_enabled') !== false;
  }

  /** Kill switch for narrator dual voice system. Default: enabled. */
  function useNarratorDualVoiceFlag(): boolean {
    return useFlag('ff_narrator_dual_voice') !== false;
  }

  // -- Wave 3: B2B + Security ----------------------------------------------

  /** Kill switch for crisis assistance features. Default: enabled. */
  function useCrisisAssistanceFlag(): boolean {
    return useFlag('ff_crisis_assistance') !== false;
  }

  /** Kill switch for practitioner portal. Default: enabled. */
  function usePractitionerPortalFlag(): boolean {
    return useFlag('ff_practitioner_portal') !== false;
  }

  /** Feature gate for practitioner access entry point. Default: DISABLED. */
  function usePractitionerAccessVisibleFlag(): boolean {
    return useFlag('ff_practitioner_access_visible') === true;
  }

  /** Kill switch for Turnstile bot protection. Default: enabled. */
  function useTurnstileEnabledFlag(): boolean {
    return useFlag('ff_turnstile_enabled') !== false;
  }

  // -- Wave 4: Insurance ---------------------------------------------------

  /** Kill switch for family plans. Default: enabled. */
  function useFamilyPlansFlag(): boolean {
    return useFlag('ff_family_plans') !== false;
  }

  /** Kill switch for trial prompts. Default: enabled. */
  function useTrialPromptsFlag(): boolean {
    return useFlag('ff_trial_prompts') !== false;
  }

  // -- Wave 5: Style Analysis -----------------------------------------------

  /** Kill switch for AI detection section (DD-2000). Default: enabled. */
  function useAiDetectionFlag(): boolean {
    return useFlag('dd-feat-ai-detection') !== false;
  }

  /** Kill switch for style consistency section (DD-1999). Default: enabled. */
  function useStyleConsistencyFlag(): boolean {
    return useFlag('dd-feat-style-consistency') !== false;
  }

  // -- Wave 6: Engagement + Growth --------------------------------------------

  /** Kill switch for habit engine (#2682). Default: enabled. */
  function useHabitEngineFlag(): boolean {
    return useFlag('dd-feat-habit-engine') !== false;
  }

  /** Kill switch for dramacards (#2682). Default: enabled. */
  function useDramaCardsFlag(): boolean {
    return useFlag('dd-feat-dramacards') !== false;
  }

  /** Kill switch for referral program — client-side companion to server FF_REFERRAL_ENABLED (#2682). Default: enabled. */
  function useReferralEnabledFlag(): boolean {
    return useFlag('ff_referral_enabled') !== false;
  }

  return {
    useGoogleSignInFlag,
    useEmailCaptureFlag,
    useSignupVariantFlag,
    useV8LetterThrowingFlag,
    useL3AccordionsFlag,
    usePatternSpotlightFlag,
    useTransformationInsightFlag,
    useSharedLinksFlag,
    useGroupAnalysisFlag,
    useHistoryPatternsFlag,
    useMultimodalFlag,
    useGenerativeResultsFlag,
    useOnboardingFlowFlag,
    useReEngagementWhatsNewFlag,
    useDramaPlusConversionUxFlag,
    useStreamingResultsFlag,
    useSharingEnabledFlag,
    useOgImageGenerationFlag,
    useSelfHealingValidationFlag,
    useSduiRenderingFlag,
    useRewritesEnabledFlag,
    useNarratorDualVoiceFlag,
    useCrisisAssistanceFlag,
    usePractitionerPortalFlag,
    usePractitionerAccessVisibleFlag,
    useTurnstileEnabledFlag,
    useFamilyPlansFlag,
    useTrialPromptsFlag,
    useAiDetectionFlag,
    useStyleConsistencyFlag,
    useHabitEngineFlag,
    useDramaCardsFlag,
    useReferralEnabledFlag,
  };
}
